import { useEffect, useState } from "react";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";
import { useSiteSettings } from "@/lib/siteSettings";

function FloatingOrbs() {
  return (
    <>
      <div className="absolute top-[15%] left-[10%] w-64 h-64 rounded-full bg-white/[0.03] blur-3xl animate-float-1" />
      <div className="absolute bottom-[25%] right-[15%] w-72 h-72 rounded-full bg-silver-300/[0.03] blur-3xl animate-float-2" />
      <div className="absolute top-[60%] left-[50%] w-52 h-52 rounded-full bg-white/[0.02] blur-2xl animate-float-3" />
    </>
  );
}

function TechScanLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.04]">
      <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-scan-line" />
    </div>
  );
}

function TechParticles() {
  const particles = [
    { left: "14%", top: "24%", delay: "0s", size: 2 },
    { left: "30%", top: "52%", delay: "1.4s", size: 3 },
    { left: "46%", top: "34%", delay: "2.2s", size: 2 },
    { left: "54%", top: "68%", delay: "0.8s", size: 1.5 },
    { left: "74%", top: "16%", delay: "1.8s", size: 2.5 },
    { left: "86%", top: "46%", delay: "2.5s", size: 2 },
    { left: "12%", top: "78%", delay: "3.0s", size: 1.5 },
    { left: "66%", top: "82%", delay: "1.1s", size: 2 },
    { left: "80%", top: "32%", delay: "2.9s", size: 3 },
    { left: "36%", top: "90%", delay: "0.6s", size: 1.5 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none hidden md:block">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white/60 animate-tech-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: `${3 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function MembershipHero() {
  const [loaded, setLoaded] = useState(false);
  const { settings } = useSiteSettings();
  const applicationsOpen = settings.membership.applications_open;
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative bg-navy-950 overflow-hidden">
      <FloatingOrbs />
      <TechScanLine />
      <TechParticles />

      <div className="absolute inset-0">
        <SmartImage
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20digital%20network%20visualization%20with%20interconnected%20glowing%20nodes%20and%20flowing%20data%20particles%20on%20deep%20dark%20navy%20background%2C%20geometric%20lattice%20structure%20with%20silver%20white%20light%20trails%2C%20holographic%20technology%20concept%20art%2C%20high%20contrast%20minimalist%20style%2C%20professional%20corporate%20aesthetic%2C%20clean%20elegant%20composition%20without%20people&width=1400&height=700&seq=hero-membership&orientation=landscape"
          alt="會員交流活動"
          className="w-full h-full object-cover opacity-40"
          hideOnError
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="w-full h-full animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16 pt-32 pb-10 md:pt-40 md:pb-14">
        <div className="max-w-4xl">
          <p
            className={`text-sm font-medium tracking-[0.2em] text-silver-400 uppercase mb-4 transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Membership
          </p>
          <h1
            className={`font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6 transition-all duration-700 delay-150 animate-text-glow ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {applicationsOpen ? "加入 TAIAA" : "與 TAIAA"}
            <br />
            <span className="text-silver-300">共創產業未來</span>
          </h1>
          <p
            className={`text-silver-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8 transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {applicationsOpen
              ? "成為 TAIAA 會員，與台灣 AI 生態系的核心力量並肩前行。我們連結產官學研，協助會員掌握趨勢、拓展資源、推動 AI 在各領域的負責任應用。"
              : "TAIAA 會員採邀請制，由理監事與現有會員推薦、秘書處邀請加入。會員與台灣 AI 生態系的核心力量並肩前行：我們連結產官學研，協助會員掌握趨勢、拓展資源、推動 AI 在各領域的負責任應用。"}
          </p>

          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-[450ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <a
              href="#join"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
            >
              {applicationsOpen ? "線上申請入會" : "入會方式"}
              <Icon className="ri-arrow-down-line" />
            </a>
            <a
              href="#benefits"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              會員權益說明
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}